import React, { createContext, memo, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BotGalleryModal from '../components/bots/BotGalleryModal';

const BotsContext = createContext(null);
// Closed nearly all the time: drawn again only when it opens or closes.
const Gallery = memo(BotGalleryModal);

const NOOP = { openBot: () => {}, openGallery: () => {} };
const NO_OPTIONS = Object.freeze({});

/** Open a bot's chat or the bot gallery from anywhere inside the app shell. */
export const useBots = () => useContext(BotsContext) || NOOP;

export const botPath = (botId, projectId) =>
  `/app/bots/${botId}${projectId ? `?project=${encodeURIComponent(projectId)}` : ''}`;

export const BotsProvider = ({ children }) => {
  const navigate = useNavigate();
  // The router's navigate changes with the path: read the current one, so the value
  // (and every useBots() caller, also in hidden tabs) stays the same across navigations.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // null = closed; otherwise { project, taskId } the new bot should join.
  const [gallery, setGallery] = useState(null);

  const value = useMemo(() => ({
    // projectId: open the bot's chats in that project (else its last project).
    openBot: (id, { projectId } = {}) => navigateRef.current(botPath(id, projectId)),
    openGallery: (options = {}) => setGallery(options),
  }), []);
  const closeGallery = useCallback(() => setGallery(null), []);
  const onCreated = useCallback((bot) => { setGallery(null); navigateRef.current(botPath(bot.id)); }, []);

  return (
    <BotsContext.Provider value={value}>
      {children}
      <Gallery
        open={!!gallery}
        options={gallery || NO_OPTIONS}
        onClose={closeGallery}
        onCreated={onCreated}
      />
    </BotsContext.Provider>
  );
};
